/**
 * Time Formatting Utilities
 * 
 * Helper functions for formatting time durations for display in the session countdown.
 */

/**
 * Format milliseconds to MM:SS format
 * @param milliseconds Time in milliseconds
 * @returns Formatted time string (e.g., "4:32")
 */
export function formatTimeRemaining(milliseconds: number): string {
  if (milliseconds <= 0) {
    return '0:00';
  }

  const totalSeconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

/**
 * Format milliseconds to HH:MM:SS format
 * @param milliseconds Time in milliseconds
 * @returns Formatted time string (e.g., "1:04:32")
 */
export function formatTimeRemainingLong(milliseconds: number): string {
  if (milliseconds <= 0) {
    return '0:00:00';
  }

  const totalSeconds = Math.floor(milliseconds / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

/**
 * Get human-readable time description
 * @param milliseconds Time in milliseconds
 * @returns Human-readable string (e.g., "5 minutes")
 */
export function getTimeDescription(milliseconds: number): string {
  if (milliseconds <= 0) {
    return 'expired';
  }

  const totalSeconds = Math.floor(milliseconds / 1000);
  
  if (totalSeconds < 60) {
    return `${totalSeconds} second${totalSeconds !== 1 ? 's' : ''}`;
  }

  const minutes = Math.floor(totalSeconds / 60);
  if (minutes < 60) {
    return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
  }

  const hours = Math.floor(minutes / 60);
  return `${hours} hour${hours !== 1 ? 's' : ''}`;
}

/**
 * Get urgency level based on time remaining
 * @param milliseconds Time in milliseconds
 * @returns Urgency level ('safe', 'warning', 'critical')
 */
export function getUrgencyLevel(milliseconds: number): 'safe' | 'warning' | 'critical' {
  if (milliseconds <= 60000) {
    return 'critical'; // < 1 minute
  }
  if (milliseconds <= 3 * 60 * 1000) {
    return 'warning'; // < 3 minutes
  }
  return 'safe'; // > 3 minutes
}

export default {
  formatTimeRemaining,
  formatTimeRemainingLong,
  getTimeDescription,
  getUrgencyLevel
};

